const API_URL = import.meta.env.VITE_API_URL ?? (import.meta.env.DEV ? 'http://localhost:3001' : '')

async function fetchWithAuth(path: string, options: RequestInit = {}) {
  const token = await (window as any).Clerk?.session?.getToken()
  
  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
  })
  
  if (!response.ok) {
    throw new Error(`API error: ${response.status} ${response.statusText}`)
  }
  
  return response.json()
}

export const api = {
  // Health
  health: () => fetchWithAuth('/api/health'),
  
  // Users
  getMe: () => fetchWithAuth('/api/users/me'),
  getMyAssignments: () => fetchWithAuth('/api/users/me/assignments'),
  getUsers: (params?: { limit?: number; offset?: number }) => {
    const searchParams = new URLSearchParams()
    if (params?.limit) searchParams.append('limit', params.limit.toString())
    if (params?.offset) searchParams.append('offset', params.offset.toString())
    const query = searchParams.toString()
    return fetchWithAuth(`/api/users${query ? `?${query}` : ''}`)
  },
  getUserProgress: (id: string, conventionId?: string) => {
    const query = conventionId ? `?convention_id=${conventionId}` : ''
    return fetchWithAuth(`/api/users/${id}/progress${query}`)
  },
  updateUserRole: (id: string, role: string) =>
    fetchWithAuth(`/api/users/${id}/role`, { method: 'POST', body: JSON.stringify({ role }) }),
  updateUserAssignments: (id: string, assignments: Array<{ crew: string; team: string; role: string; primary?: boolean }>) =>
    fetchWithAuth(`/api/users/${id}/assignments`, { method: 'POST', body: JSON.stringify({ assignments }) }),
  bulkUpdateUserRoles: (userIds: string[], role: string) =>
    fetchWithAuth('/api/users/bulk/role', { method: 'POST', body: JSON.stringify({ userIds, role }) }),
  bulkAssignUsers: (userIds: string[], assignments: Array<{ crew: string; team: string; role: string; primary?: boolean }>) =>
    fetchWithAuth('/api/users/bulk/assignments', { method: 'POST', body: JSON.stringify({ userIds, assignments }) }),
  
  // Progress
  getProgress: (conventionId?: string) => {
    const query = conventionId ? `?convention_id=${conventionId}` : ''
    return fetchWithAuth(`/api/progress${query}`)
  },
  updateProgress: (data: {
    module_id: string
    status: string
    percent_complete?: number
    last_slide_index?: number
    convention_id?: string
  }) => fetchWithAuth('/api/progress', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  // Knowledge checks
  getKnowledgeCheckScores: (params?: { crew?: string; team?: string; convention_id?: string }) => {
    const searchParams = new URLSearchParams()
    if (params?.crew) searchParams.append('crew', params.crew)
    if (params?.team) searchParams.append('team', params.team)
    if (params?.convention_id) searchParams.append('convention_id', params.convention_id)
    const query = searchParams.toString()
    return fetchWithAuth(`/api/knowledge-checks/scores${query ? `?${query}` : ''}`)
  },
  getKnowledgeCheckAttempts: (params?: { module_id?: string; convention_id?: string }) => {
    const searchParams = new URLSearchParams()
    if (params?.module_id) searchParams.append('module_id', params.module_id)
    if (params?.convention_id) searchParams.append('convention_id', params.convention_id)
    const query = searchParams.toString()
    return fetchWithAuth(`/api/knowledge-checks/attempts${query ? `?${query}` : ''}`)
  },
  submitKnowledgeCheckAttempt: (data: {
    module_id: string
    kc_id: string
    choice_id: string
    correct: boolean
    convention_id?: string
  }) => fetchWithAuth('/api/knowledge-checks/attempts', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  // Invitations (facilitator only)
  getInvitations: (status?: string) => {
    const query = status ? `?status=${status}` : ''
    return fetchWithAuth(`/api/invitations${query}`)
  },
  createInvitation: (data: {
    emailAddress: string
    role?: string
    assignments?: Array<{ crew: string; team: string; role: string; primary?: boolean }>
  }) => fetchWithAuth('/api/invitations', { method: 'POST', body: JSON.stringify(data) }),
  createBulkInvitations: (invitations: Array<{
    emailAddress: string
    role?: string
    assignments?: Array<{ crew: string; team: string; role: string; primary?: boolean }>
  }>) => fetchWithAuth('/api/invitations/bulk', { method: 'POST', body: JSON.stringify({ invitations }) }),
  resendInvitation: (id: string) => fetchWithAuth(`/api/invitations/${id}/resend`, { method: 'POST' }),
  revokeInvitation: (id: string) => fetchWithAuth(`/api/invitations/${id}`, { method: 'DELETE' }),

  // Progress overview (facilitator only)
  getProgressOverview: (params?: { convention_id?: string; crew?: string; team?: string; role?: string }) => {
    const searchParams = new URLSearchParams()
    if (params?.convention_id) searchParams.append('convention_id', params.convention_id)
    if (params?.crew) searchParams.append('crew', params.crew)
    if (params?.team) searchParams.append('team', params.team)
    if (params?.role) searchParams.append('role', params.role)
    const query = searchParams.toString()
    return fetchWithAuth(`/api/progress/overview${query ? `?${query}` : ''}`)
  },

  // Content
  getContentIndex: () => fetchWithAuth('/api/content/index'),
  getModules: (params?: { crew?: string; team?: string; role?: string; stage?: number; phase?: string }) => {
    const searchParams = new URLSearchParams()
    if (params?.crew) searchParams.append('crew', params.crew)
    if (params?.team) searchParams.append('team', params.team)
    if (params?.role) searchParams.append('role', params.role)
    if (params?.stage) searchParams.append('stage', params.stage.toString())
    if (params?.phase) searchParams.append('phase', params.phase)
    const query = searchParams.toString()
    return fetchWithAuth(`/api/content/modules${query ? `?${query}` : ''}`)
  },
  getModule: (id: string) => fetchWithAuth(`/api/content/modules/${id}`),
  getPhases: (crew: string, team: string) =>
    fetchWithAuth(`/api/content/phases?crew=${crew}&team=${team}`),
  getTeam: (crew: string, team: string) =>
    fetchWithAuth(`/api/content/teams?crew=${crew}&team=${team}`),
  getPhaseSchedule: (convention = 'ic-manila-2026') =>
    fetchWithAuth(`/api/content/phase-schedule?convention=${convention}`),

  // Practice sessions
  createPracticeSession: (data: {
    crew: string
    team: string
    convention_id?: string
  }) => fetchWithAuth('/api/practice/sessions', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  getPracticeSession: (id: string) =>
    fetchWithAuth(`/api/practice/sessions/${id}`),
  joinPracticeSession: (id: string) =>
    fetchWithAuth(`/api/practice/sessions/${id}/join`, { method: 'POST' }),
  pushScenario: (id: string, data: {
    scenario_id: string
    scenario_data?: unknown
  }) => fetchWithAuth(`/api/practice/sessions/${id}/scenario`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  endPracticeSession: (id: string) =>
    fetchWithAuth(`/api/practice/sessions/${id}/end`, { method: 'POST' }),

  // Scenarios
  getScenarios: (params?: { crew?: string; team?: string; role?: string; stage?: string }) => {
    const searchParams = new URLSearchParams()
    if (params?.crew) searchParams.append('crew', params.crew)
    if (params?.team) searchParams.append('team', params.team)
    if (params?.role) searchParams.append('role', params.role)
    if (params?.stage) searchParams.append('stage', params.stage)
    const query = searchParams.toString()
    return fetchWithAuth(`/api/content/scenarios${query ? `?${query}` : ''}`)
  },

  submitPracticeResponse: (sessionId: string, data: {
    scenario_id: string
    response_text?: string
  }) => fetchWithAuth(`/api/practice/sessions/${sessionId}/responses`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  getPracticeResponses: (sessionId: string, scenarioId?: string) => {
    const query = scenarioId ? `?scenario_id=${scenarioId}` : ''
    return fetchWithAuth(`/api/practice/sessions/${sessionId}/responses${query}`)
  },

  // Search
  search: (params: { q: string; crew?: string; team?: string; phase?: string }) => {
    const searchParams = new URLSearchParams()
    searchParams.append('q', params.q)
    if (params.crew) searchParams.append('crew', params.crew)
    if (params.team) searchParams.append('team', params.team)
    if (params.phase) searchParams.append('phase', params.phase)
    return fetchWithAuth(`/api/content/search?${searchParams.toString()}`)
  },
}
